import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../../gen-src/Localize';
import { ListAndMenuColors, TextColors } from '../../styles/Colors';
import { PreventUserSelect } from '../../styles/PreventUserSelect';
import { TextEllipsis } from '../../styles/TextEllipsis';
import { makeTestId } from '../../utils/Declarative';
import { isElementOverflownX } from '../../utils/DomUtil';
import { Img } from '../DomWidgets';
import { ListAndMenuItemHeight, ListAndMenuPadding } from '../List/ListStyles';
import { DisplayValueFormatterProps } from './DisplayValueFormatter';

const GroupLabelClass = 'grouplabel';
const HasDetailClass = 'hasdetail';
export const HighlightClass = 'highlight';
export const ActiveClass = 'active';

const StyleName = TypeStyle.style({
  borderRadius: 2,
  boxSizing: 'border-box',
  height: ListAndMenuItemHeight,
  lineHeight: `${ListAndMenuItemHeight}px`,
  whiteSpace: 'nowrap',
  ...PreventUserSelect,
  $nest: {
    // item container
    '[role^="menuitem"] > &, [role="option"] > &': { // role=option is for the legacy-deprecated DropdownWidget so we need both
      paddingLeft: ListAndMenuPadding,
      paddingRight: ListAndMenuPadding,
    },
    [`&.${GroupLabelClass}`]: { fontWeight: 'bold' },
    [`&.${HasDetailClass}`]: {
      fontWeight: 'bold',
      height: 'auto',
      minHeight: ListAndMenuItemHeight * 2,
      lineHeight: `${ListAndMenuItemHeight - 4}px`,
      paddingBottom: 4,
      paddingTop: 4,
      whiteSpace: 'normal',
    },

    // interactive states
    [`&.${HighlightClass}, &.${ActiveClass}`]: {
      outline: `1px solid ${ListAndMenuColors.ActiveBorder}`,
      outlineOffset: -1,
    },
    [`&.${HighlightClass}`]: {
      backgroundColor: ListAndMenuColors.Hover,
    },
    [`&.${ActiveClass}`]: {
      backgroundColor: ListAndMenuColors.Active,
    },
    '[role^="menuitem"] > &:active, [role="option"] > &:active': {
      backgroundColor: ListAndMenuColors.Press,
    },
    '[aria-disabled="true"] > &': {
      backgroundColor: 'transparent !important',
      color: `${ListAndMenuColors.DisabledText} !important`,
    },
  }
});

// item icon, text, and annotation
const GutterStyleName = TypeStyle.style({
  alignItems: 'center',
  display: 'inline-flex',
  flexShrink: 0,
  flexGrow: 0,
  marginLeft: -ListAndMenuPadding + 1, // +1 for border
  justifyContent: 'center',
  width: 22,  // 10px icon + 2 * ListAndMenuPadding, or 14px icon + 2 * 4px spacing, same same!
});
const TextStyleName = TypeStyle.style({ ...TextEllipsis, flexGrow: 1 });
const AnnotationStyleName = TypeStyle.style({
  alignItems: 'center',
  color: TextColors.Neutral,
  display: 'inline-flex',
  flexShrink: 0,
  marginLeft: 8,
});
const DetailStyleName = TypeStyle.style({ fontWeight: 'normal' });

function isUrl(s?: unknown): s is string {
  try {
    new URL(s as string);
  } catch {
    return false;
  }
  return true;
}

function renderIcon(Icon: DisplayValueFormatterProps['icon'] & DisplayValueFormatterProps['annotation']): React.ReactNode {
  if (isUrl(Icon)) {
    return <Img src={Icon} aria-hidden />;
  } else if (typeof Icon === 'string') {
    return Icon;
  } else if (Icon) {
    const iconAriaPropsForTesting = { role: 'img' }; // tsc complains if this is assigned inline and we don't want to augment the external typedef
    return <Icon size={14} aria-hidden {...iconAriaPropsForTesting} />;
  } else {
    return null;
  }
}

export type TextFormatterState = { title?: string };
export { DisplayValueFormatterProps };

/**
 * @param displayValue text value to display
 * @param tooltipOverride tooltip to display (overrides default overflow-tooltip behavior)
 */
export const TextMenuDisplayFormatter = (displayValue?: string, tooltipOverride?: string): React.ComponentClass<DisplayValueFormatterProps> => {
  return class TextFormatterWidget extends React.PureComponent<DisplayValueFormatterProps, TextFormatterState> {
    public state: TextFormatterState = { title: tooltipOverride };

    private handleMouseOver: React.MouseEventHandler<HTMLElement> = e => {
      if (tooltipOverride) {
        // NOTE: The custom title will override the default title when the displayed value within the menu item is truncated.
        return;
      }

      const textNodes = Array.from(e.currentTarget.querySelectorAll(`.${TextStyleName}, .${DetailStyleName}`));
      this.setState({ title: textNodes.some(isElementOverflownX) ? textNodes.map(n => n.textContent).join(Messages.colonSeparator()) : undefined });
    }

    public render(): React.ReactNode {
      const showGutter = this.props.showIconGutter && !this.props.isGroupLabel;
      return (
        // eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
        <div
          className={TypeStyle.classes(StyleName, this.props.isGroupLabel ? GroupLabelClass : {
            [ActiveClass]: this.props.active,
            [HasDetailClass]: !!this.props.detail,
            [HighlightClass]: this.props.isHighlighted,
          })}
          data-tb-test-id={makeTestId(this.props.testId, 'TextMenuItem')}
          onMouseOver={this.handleMouseOver}
          title={this.state.title}
        >
          <div style={{ display: 'flex' }}>
            {showGutter && <span key='gutter' className={GutterStyleName}>
              {renderIcon(this.props.icon)}
            </span>}
            <span key='text' className={TextStyleName}>{displayValue /* this has to be in a span for text eliding to work in a flexbox */}</span>
            {this.props.annotation && <span key='annotation' className={AnnotationStyleName}>
              {renderIcon(this.props.annotation)}
            </span>}
          </div>

          {this.props.detail && <div key='detail' className={DetailStyleName}>
            {this.props.detail}
          </div>}
        </div>
      );
    }
  };
};
